import React, { useEffect, useMemo, useState } from 'react';
import Larry from './Larry';
import usePageTitle from '../hooks/usePageTitle';
import {
  getPeople, requestFriend, acceptFriendRequest, rejectFriendRequest, unfriend,
} from '../services/api';

/**
 * The club directory — who you can friend, and where each relationship stands.
 *
 * Fed by GET /api/people, which deliberately carries no email addresses (the
 * admin roster does; this is open to every approved member).
 */
export default function PeoplePage() {
  usePageTitle('People');
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPeople();
      setPeople(res?.people || []);
    } catch (err) {
      setError(err.message || 'Failed to load people');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Update one row in place rather than refetching the whole roster (247 rows).
  const patch = (memberId, changes) => setPeople(prev => prev.map(
    p => (String(p.member_id) === String(memberId) ? { ...p, ...changes } : p)
  ));

  const act = async (person, fn, changes) => {
    setBusyId(person.member_id);
    setActionError('');
    try {
      await fn();
      patch(person.member_id, changes);
    } catch (err) {
      if (err.code === 'REQUEST_EXISTS') patch(person.member_id, { friendship_state: 'pending_out' });
      else if (err.code === 'ALREADY_FRIENDS') patch(person.member_id, { friendship_state: 'friends' });
      else setActionError(err.message || 'Something went wrong');
    } finally {
      setBusyId(null);
    }
  };

  const counts = useMemo(() => ({
    all: people.length,
    friends: people.filter(p => p.friendship_state === 'friends').length,
    requested: people.filter(p => p.friendship_state === 'pending_out').length,
  }), [people]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return people.filter((p) => {
      if (tab === 'friends' && p.friendship_state !== 'friends') return false;
      if (tab === 'requested' && p.friendship_state !== 'pending_out') return false;
      if (q && !`${p.first_name} ${p.last_name}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [people, search, tab]);

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="rounded-xl animate-pulse" style={{ height: 60, background: 'var(--bg-input)' }} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-paper p-8 flex flex-col items-center text-center gap-3">
        <Larry mood="sad" size={64} message={error} />
        <button onClick={load} className="btn-bouncy text-sm px-5 py-2">Try again</button>
      </div>
    );
  }

  return (
    <div className="space-y-5 stagger-in">
      {/* No section header here: PageShell already renders the "People" title,
          and a second one read as a duplicate heading. */}

      <div className="flex flex-wrap items-center gap-2">
        {[
          { id: 'all', label: 'All' },
          { id: 'friends', label: 'Friends' },
          { id: 'requested', label: 'Requested' },
        ].map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`pill-tab text-sm ${tab === t.id ? 'active' : ''}`}
          >
            {t.label}
            <span className="font-mono text-xs ml-1.5" style={{ opacity: 0.7 }}>
              {counts[t.id]}
            </span>
          </button>
        ))}
      </div>

      <input
        type="text"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Search by name…"
        aria-label="Search people by name"
        style={{
          width: '100%',
          background: 'var(--bg-input)',
          border: '1px solid var(--border-subtle)',
          color: 'var(--text-primary)',
          borderRadius: '0.5rem',
          padding: '0.625rem 0.875rem',
          fontSize: '0.875rem',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />

      {actionError && (
        <p
          className="px-3 py-2 rounded-lg text-sm"
          style={{
            background: 'var(--color-danger-dim)',
            border: '1px solid var(--color-danger-border)',
            color: 'var(--color-danger)',
          }}
        >
          {actionError}
        </p>
      )}

      {filtered.length === 0 ? (
        <div
          className="rounded-xl p-10 text-center"
          style={{ background: 'var(--bg-surface)', border: '1px dashed var(--border-subtle)' }}
        >
          <p className="text-sm" style={{ color: 'var(--text-faint)' }}>
            {search.trim()
              ? 'Nobody matches that name.'
              : tab === 'friends'
                ? 'No friends yet — ask someone from the All tab.'
                : tab === 'requested'
                  ? 'No pending requests you have sent.'
                  : 'Nobody here yet.'}
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((p) => {
            const name = `${p.first_name} ${p.last_name}`.trim() || 'Member';
            const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
            const busy = String(busyId) === String(p.member_id);

            return (
              <li
                key={p.member_id}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5"
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
              >
                <div
                  className="flex-shrink-0 flex items-center justify-center rounded-full text-xs font-bold"
                  style={{ width: 32, height: 32, background: 'var(--accent-dim)', color: 'var(--primary)' }}
                >
                  {initials}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{name}</p>
                  {p.is_provisional && (
                    <p className="text-xs" style={{ color: 'var(--text-faint)' }}>Hasn&apos;t joined yet</p>
                  )}
                </div>

                <PersonAction person={p} busy={busy} act={act} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** The one action available for a given relationship state. */
function PersonAction({ person: p, busy, act }) {
  const ghost = {
    background: 'var(--bg-input)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)',
  };
  const cls = 'text-xs font-semibold px-2.5 py-1.5 rounded-lg flex-shrink-0 transition-opacity hover:opacity-80 disabled:opacity-50';

  // A provisional member has never signed in, so there is nobody to accept.
  if (p.is_provisional) {
    return <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-faint)' }}>—</span>;
  }

  if (p.friendship_state === 'friends') {
    return (
      <button
        type="button" disabled={busy} className={cls} style={ghost}
        onClick={() => act(p, () => unfriend(p.member_id), { friendship_state: 'none' })}
      >
        Friends ✓
      </button>
    );
  }

  if (p.friendship_state === 'pending_out') {
    return <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>Requested</span>;
  }

  if (p.friendship_state === 'pending_in') {
    return (
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          type="button" disabled={busy} className={cls}
          style={{
            background: 'var(--color-success-dim)',
            border: '1px solid var(--color-success-border)',
            color: 'var(--color-success)',
          }}
          onClick={() => act(p, () => acceptFriendRequest(p.friendship_id), { friendship_state: 'friends' })}
        >
          Accept
        </button>
        <button
          type="button" disabled={busy} className={cls} style={ghost}
          onClick={() => act(p, () => rejectFriendRequest(p.friendship_id), { friendship_state: 'none' })}
        >
          Reject
        </button>
      </div>
    );
  }

  return (
    <button
      type="button" disabled={busy} className={cls}
      style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)', color: 'var(--primary)' }}
      onClick={() => act(p, () => requestFriend(p.member_id), { friendship_state: 'pending_out' })}
    >
      Add friend
    </button>
  );
}
